import gsap from 'gsap'
import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants.js'

const SELECTORS = {
  pageContainer: '.page-container',
  signInButton: '.mectrl_trigger',
  nestedModal: '.js-nested-modal',
  outerModal: '.js-nested-modal__outer',
  outerModalOpenButton: '.js-nested-modal__outer__open-btn',
  outerModalCloseButton: '.js-nested-modal__outer__close-btn',
  outerModalPrev: '.js-nested-modal__outer__nav-arrows--prev',
  outerModalNext: '.js-nested-modal__outer__nav-arrows--next',
  outerModalFlagCount: '.js-nested-modal__outer__flag-count',
  outerModalFlagTotal: '.js-nested-modal__outer__flag-total',
  innerModal: '.js-nested-modal__inner',
  innerModalOpenButton: '.js-nested-modal__inner__open-btn',
  innerModalCloseButton: '.js-nested-modal__inner__close-btn',
  innerModalScrim: '.js-nested-modal__inner__scrim',
  flagWrapper: '.js-nested-modal__inner__content__flags',
  flagItem: '.js-nested-modal__inner__content__flags__item',
  modalCurtainContainer: '.js-nested-modal__curtain-container',
  modalCurtain: '.js-nested-modal__curtain',
  interviewPortrait: '.js-nested-modal__portrait',
  interviewName: '.js-nested-modal__text-name',
  interviewPronouns: '.js-nested-modal__text-pronouns',
  interviewIdentity: '.js-nested-modal__text-identity',
  interviewTranscript: '.js-nested-modal__text-transcript',
  interviewFlag: '.js-nested-modal__outer__flag-img',
  interviewFlagName: '.js-nested-modal__outer__flag-name',
  interviewAudio: '.js-audio-player'
}

const EVENTS = {
  FLAG_SELECTED: 'flag-selected'
}

/* We need to turn off all focusable elements in the page container while the modal is open to trap focus in the modal */
const FOCUSABLE_SELECTORS = [
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  '*[tabindex]',
  '*[contenteditable]'
].join(',')

class NestedModal {
  constructor (element) {
    this.element = element
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER
    this.data = null

    this.signInButton = null
    this.pageContainer = document.querySelector(SELECTORS.pageContainer)

    // Array of elements to have focus remove when opening modal to create the focus trap.
    this.focusableElements = Array.from(
      this.pageContainer.querySelectorAll(FOCUSABLE_SELECTORS)
    )

    // Outer modal selectors.
    this.outerModalOpenButton = document.querySelector(SELECTORS.outerModalOpenButton)
    this.outerModalCloseButton = this.element.querySelector(SELECTORS.outerModalCloseButton)
    this.outerModal = this.element.querySelector(SELECTORS.outerModal)
    this.outerModalPrev = this.element.querySelector(SELECTORS.outerModalPrev)
    this.outerModalNext = this.element.querySelector(SELECTORS.outerModalNext)
    this.outerModalFlagCount = this.element.querySelector(SELECTORS.outerModalFlagCount)
    this.outerModalFlagTotal = this.element.querySelector(SELECTORS.outerModalFlagTotal)

    // Interview selectors.
    this.interviewPortrait = this.element.querySelector(SELECTORS.interviewPortrait)
    this.interviewName = this.element.querySelector(SELECTORS.interviewName)
    this.interviewPronouns = this.element.querySelector(SELECTORS.interviewPronouns)
    this.interviewIdentity = this.element.querySelector(SELECTORS.interviewIdentity)
    this.interviewTranscript = this.element.querySelector(SELECTORS.interviewTranscript)
    this.interviewFlag = this.element.querySelector(SELECTORS.interviewFlag)
    this.interviewFlagName = this.element.querySelector(SELECTORS.interviewFlagName)
    this.interviewAudio = this.element.querySelector(SELECTORS.interviewAudio)

    this.modalCurtainContainer = this.element.querySelector(SELECTORS.modalCurtainContainer)
    this.flagWrapper = this.element.querySelector(SELECTORS.flagWrapper)

    // Inner modal selectors.
    this.innerModalOpenButton = this.element.querySelector(SELECTORS.innerModalOpenButton)
    this.innerModalCloseButton = this.element.querySelector(SELECTORS.innerModalCloseButton)
    this.innerModalScrim = this.element.querySelector(SELECTORS.innerModalScrim)
    this.innerModal = this.element.querySelector(SELECTORS.innerModal)
    this.innerModalContent = this.innerModal.querySelector('.js-nested-modal__inner__content')

    this.templateUri = this.modalCurtainContainer.dataset.templateDirectoryUri
    this.init()
  }

  init() {
    this.registerListeners()
  }

  registerListeners() {
    this.outerModalOpenButton.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.openOuterModal)
    this.innerModalOpenButton.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.openInnerModal)
    this.outerModalCloseButton.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.closeOuterModal)
    this.innerModalCloseButton.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.closeInnerModal)
    this.innerModalScrim.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.closeInnerModal)

    document.addEventListener(GLOBAL_CONSTANTS.EVENTS.KEYDOWN, this.keyDownHandler)

    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.PRIDE_DATA_LOADED, (data) => {
      this.data = data
      this.populateFlags(this.data)
      this.registerFlagClickHandlers()

      // Set total number of flags for outer model nav.
      this.outerModalFlagTotal.innerHTML = this.data.length
    })
    this.emitter.on(EVENTS.FLAG_SELECTED, this.handleFlagSelected)
  }

  // Removes focus from all focusable elements in page container
  removeFocusable = () => {
    for (const elem of this.focusableElements) {
      elem.setAttribute('tabindex', '-1')
    }

    this.signInButton = document.querySelector('.mectrl_trigger')
    if (this.signInButton != null) {
      this.signInButton.setAttribute('tabindex', '-1')
    }
  }

  // Adds focus to all focusable elements in page container
  addFocusable = () => {
    for (const elem of this.focusableElements) {
      elem.removeAttribute('tabindex')
    }

    if (this.signInButton != null) {
      this.signInButton.removeAttribute('tabindex')
    }
  }

  // Get the focusable elements in the outer modal
  getOuterModalFocusable() {
    this.outerModalFocusableElements = Array.from(
      this.outerModal.querySelectorAll(FOCUSABLE_SELECTORS)
    )
  }

  // Remove focus from the focusable elements in the outer modal
  removeOuterModalFocusable() {
    this.getOuterModalFocusable()

    for (const elem of this.outerModalFocusableElements) {
      elem.setAttribute('tabindex', '-1')
    }
  }

  // Add focus back to the focusable elements in the outer modal
  addOuterModalFocusable() {
    for (const elem of this.outerModalFocusableElements) {
      elem.removeAttribute('tabindex')
    }
  }

  // Open story modal. ("Play the story" link)
  openOuterModal = (event) => {
    this.removeFocusable()
    this.pageContainer.setAttribute('aria-hidden', 'true')

    document.body.classList.add(GLOBAL_CONSTANTS.CLASSES.OPEN)

    setTimeout(() => {
      this.outerModal.classList.add(GLOBAL_CONSTANTS.CLASSES.OPEN)
      this.outerModal.setAttribute('aria-hidden', false)
    }, GLOBAL_CONSTANTS.TIMING.SLOWER_ANIM_TIME / 3)

    const target = event.target
    const targetFlag = target.dataset.flag
    this.showModalCurtain(targetFlag)

    // Set interview data based on flag.
    this.handleDataChange(targetFlag)

    this.outerModalCloseButton.focus()
  }

  // Close story modal.
  closeOuterModal = () => {
    this.addFocusable()
    this.pageContainer.setAttribute('aria-hidden', 'false')

    document.body.classList.remove(GLOBAL_CONSTANTS.CLASSES.OPEN)
    this.outerModal.classList.remove(GLOBAL_CONSTANTS.CLASSES.OPEN)
    this.interviewAudio.querySelector('audio').pause()
    this.outerModal.setAttribute('aria-hidden', true)

    // Return focus to button used to open modal so that screen reader returns to the place before opening the modal
    this.outerModalOpenButton.focus()
  }

  // Open flag gallery inner modal. ("View all flags" link)
  openInnerModal = () => {
    this.removeOuterModalFocusable()

    this.innerModalCloseButton.removeAttribute('tabindex')

    this.innerModal.classList.add(GLOBAL_CONSTANTS.CLASSES.OPEN)
    this.innerModal.setAttribute('aria-hidden', false)
    this.interviewAudio.querySelector('audio').pause()
    this.innerModalCloseButton.focus()

    gsap.to(this.innerModalContent, { y: 0 })
  }

  // Close flag gallery inner modal.
  closeInnerModal = () => {
    this.innerModal.classList.remove(GLOBAL_CONSTANTS.CLASSES.OPEN)
    this.innerModal.setAttribute('aria-hidden', true)
    this.innerModalCloseButton.setAttribute('tabindex', '-1')
    this.addOuterModalFocusable()

    // return focus to button used to open modal so that screen reader returns to the place before opening the modal
    this.innerModalOpenButton.focus()

    gsap.set(this.innerModalContent, { y: '15vh' })
  }

  // Key handlers for closing modals.
  keyDownHandler = (event) => {
    if (event.key === 'Escape') {
      if (this.innerModalIsOpen()) {
        this.closeInnerModal()
      } else {
        this.closeOuterModal()
      }
    }
  }

  innerModalIsOpen = () => {
    return this.innerModal.classList.contains(GLOBAL_CONSTANTS.CLASSES.OPEN)
  }

  // Populate flags in flag gallery.
  populateFlags(data) {
    let flags = ''

    data.forEach(item => {
      flags += `<li class="js-nested-modal__inner__content__flags__item nested-modal__inner__content__flags__item" data-flag-name="${item.name}">`
      flags += `<img class="nested-modal__inner__content__flags__item__flag" src="${unlocked.theme_dir}${item.flagAsset}"/>`
      if (item.interview.portrait) flags += `<img class="nested-modal__inner__content__flags__item__portrait" src='${unlocked.theme_dir}${item.interview.portrait}'/>`
      flags += `<div class="nested-modal__inner__content__flags__name">${item.name}</div>`
      flags += '</li>'

      this.flagWrapper.innerHTML = flags
    })
  }

  registerFlagClickHandlers() {
    this.flagWrapper.querySelectorAll(SELECTORS.flagItem).forEach(flag => {
      flag.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.flagClicked, true)
    })

    this.outerModalPrev.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, el => {
      const prev = el.target.closest(SELECTORS.outerModalPrev).dataset.prev
      this.handleFlagSelected(this.data[prev].name)
    }, true)

    this.outerModalNext.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, el => {
      const next = el.target.closest(SELECTORS.outerModalNext).dataset.next
      this.handleFlagSelected(this.data[next].name)
    }, true)
  }

  flagClicked = (event) => {
    const flagName = event.currentTarget.dataset.flagName
    this.emitter.emit(EVENTS.FLAG_SELECTED, flagName)
    this.closeInnerModal()
  }

  // Flag transition helpers.
  handleFlagSelected = (flag) => {
    this.showModalCurtain(flag)

    // Change data after half the time as curtain animation.
    setTimeout(() => {
      this.handleDataChange(flag)
    }, GLOBAL_CONSTANTS.TIMING.SLOWER_ANIM_TIME / 2)
  }

  // Replace interview and nav data based on flag selected.
  handleDataChange(flag) {
    this.interviewAudio.querySelector('audio').pause()
    this.data.forEach(item => {
      if (item.name === flag) {
        const itemIndex = this.data.indexOf(item)
        this.outerModalFlagCount.innerHTML = itemIndex + 1

        if (itemIndex > 0) {
          this.outerModalPrev.disabled = false
          this.outerModalPrev.dataset.prev = itemIndex - 1
        } else {
          this.outerModalPrev.disabled = true
        }

        if (itemIndex + 1 >= this.data.length) {
          this.outerModalNext.disabled = true
        } else {
          this.outerModalNext.disabled = false
          this.outerModalNext.dataset.next = itemIndex + 1
        }

        if (item.interview.portrait) {
          this.interviewPortrait.classList.add('-show')
          this.interviewPortrait.src = this.templateUri + item.interview.portrait
        } else {
          this.interviewPortrait.classList.remove('-show')
        }
        if (item.interview.name) {
          this.interviewName.classList.add('-show')
          this.interviewName.innerHTML = item.interview.name
        } else {
          this.interviewName.classList.remove('-show')
          this.interviewName.innerHTML = ''
        }
        this.interviewPronouns.innerHTML = item.interview.pronouns
        this.interviewIdentity.innerHTML = item.interview.identity
        if (!item.interview.disclaimer) {
          this.interviewTranscript.innerHTML = item.interview.transcript
        } else {
          this.interviewTranscript.innerHTML = `
          <h2 class="nested-modal__text-share-header">Share your story</h2>
          <p class="nested-modal__text-share">We’re celebrating stories from LGBTQIA+ gamers, and we’d love to hear yours! If you fly this flag and want a chance to see your story here, drop us a note at <a href="mailto:pridegamingstories@microsoft.com">pridegamingstories@microsoft.com</a>.</p>
          <h3 class="nested-modal__text-about">About this flag</h3>
          ${item.interview.transcript !== null ? `<p>${item.interview.transcript}</p>` : ''}
          <p><small>By submitting your story via this alias, you authorize Microsoft (and its partners/licensees) to consider your story for publication (in full or part) on Microsoft owned properties and/or social media channels. You hereby represent and warrant that you are over the age of 18 and that your submission does not violate others' privacy, publicity, or other intellectual property rights. Do not submit any obscene, offensive, or unlawful content. You agree that we are under no obligation to publish your story and that we do not owe you any compensation if we do. If we are interested in publishing your story, we will ask you to provide us with a suitable release and consent. Please review the Microsoft <a href="https://go.microsoft.com/fwlink/?LinkId=521839" target="_blank">Privacy Statement</a>.<small></p>
          `
        }
        this.interviewFlag.src = this.templateUri + item.flagAsset
        this.interviewFlag.alt = `${item.name} Flag`
        this.interviewFlagName.innerHTML = `${item.name} Flag`
        if (item.interview.audioAsset) {
          this.interviewAudio.classList.add('-show')
          this.interviewAudio.querySelector('audio').src = this.templateUri + item.interview.audioAsset
          this.interviewAudio.querySelector('audio').load()
        } else {
          this.interviewAudio.classList.remove('-show')
        }
      }
    })
  }

  showModalCurtain = (flag) => {
    const imagePath = unlocked.theme_dir + this.getTransitionFlagPathByName(flag)
    const curtainEl = this.element.querySelector(SELECTORS.modalCurtain)
    const imageEl = document.createElement('img')
    imageEl.setAttribute('src', imagePath)
    curtainEl.insertAdjacentElement('afterbegin', imageEl)
    curtainEl.classList.add('nested-modal__curtain--end')

    const newCurtainEl = document.createElement('div')
    newCurtainEl.classList.add('nested-modal__curtain', 'js-nested-modal__curtain')
    this.modalCurtainContainer.insertAdjacentElement('afterbegin', newCurtainEl)

    setTimeout(() => {
      curtainEl.remove()
    }, GLOBAL_CONSTANTS.TIMING.SLOWER_ANIM_TIME)
  }

  getTransitionFlagPathByName(flagName) {
    const primaryFlag = this.data.find(d => d.isPrimaryFlag)
    let selectedFlag

    if (!flagName) {
      return primaryFlag.transitionAsset
    } else {
      selectedFlag = this.data.find(d => {
        return d.name.toLowerCase() === flagName.toLowerCase()
      })
    }
    if (!selectedFlag) {
      selectedFlag = primaryFlag
    }

    return selectedFlag.transitionAsset
  }
}

export default {
  selector: SELECTORS.nestedModal,
  Source: NestedModal
}
